import {
  getPoolUrl,
  invalidateQueries,
  type PoolParams,
  useApi,
  useDeleteQuery,
  useGetQuery,
  usePatchQuery,
  usePostQuery,
  useRouteParams
} from "@quortex/front-common"
import { useSuspenseQuery } from "@tanstack/react-query"
import {
  getAssetCollectionListUrl,
  getAssetCollectionUrl
} from "@/queries/live2Vod/playL2VAssetCollections"
import { getRecorderUrl } from "@/queries/live2Vod/playL2VRecorders"
import { usePool } from "@/queries/playPools"
import type {
  AssetParams,
  AssetUrlListExportParams,
  L2VodParams,
  PaginationParams,
  RecorderParams,
  TrashedAssetQueryParams
} from "@/queries/types"

//--------------------------------------------------------------------------------------------------
// URLs
//--------------------------------------------------------------------------------------------------

/**
 * @returns /pools/${poolUuid}/l2v/assets/${assetUuid}?org=${orgUuid}
 */
export const getAssetUrl = ({ orgUuid, poolUuid, assetUuid }: Required<AssetParams>) => {
  const params = new URLSearchParams({ org: orgUuid })
  return `/pools/${poolUuid}/l2v/assets/${assetUuid}/?${params}`
}

/**
 * @returns /pools/${poolUuid}/l2v/assets/?org=${orgUuid}&collection=${collectionUuid}
 */
export const getAssetListUrl = ({
  orgUuid,
  poolUuid,
  assetCollectionUuid
}: Required<PoolParams> & PaginationParams & { assetCollectionUuid?: string }) => {
  const params = new URLSearchParams({ org: orgUuid })
  if (assetCollectionUuid) params.set("collection_uuid", assetCollectionUuid)
  return `/pools/${poolUuid}/l2v/assets/?${params}`
}

/**
 * @returns /pools/${poolUuid}/l2v/trash/${assetUuid}/?org=${orgUuid}
 */
export const getTrashedAssetUrl = ({ orgUuid, poolUuid, assetUuid }: Required<AssetParams>) => {
  const params = new URLSearchParams({ org: orgUuid })
  return `/pools/${poolUuid}/l2v/trash/${assetUuid}/?${params}`
}

/**
 * @returns /pools/${poolUuid}/l2v/trash/?org=${orgUuid}
 */
export const getTrashedAssetListUrl = ({
  orgUuid,
  poolUuid,
  ordering,
  page,
  pageSize,
  search
}: Required<PoolParams> & PaginationParams & TrashedAssetQueryParams) => {
  const params = new URLSearchParams({ org: orgUuid })
  if (ordering) params.set("ordering", ordering)
  if (page != null) params.set("page", String(page))
  if (pageSize != null) params.set("page_size", String(pageSize))
  if (search) params.set("search", search)
  return `/pools/${poolUuid}/l2v/trash/?${params}`
}

/**
 * @returns /pools/${poolUuid}/l2v/trash/${assetUuid}/?org=${orgUuid}
 */
export const getDeleteTrashedAssetUrl = ({
  orgUuid,
  poolUuid,
  assetUuid
}: Required<AssetParams>) => {
  const params = new URLSearchParams({ org: orgUuid })
  return `/pools/${poolUuid}/l2v/trash/${assetUuid}/?${params}`
}

/**
 * @returns /pools/${poolUuid}/l2v/trash/bulk_delete/?org=${orgUuid}
 */
export const getBulkDeleteTrashedAssetUrl = ({
  orgUuid,
  poolUuid
}: Required<PoolParams> & PaginationParams) => {
  const params = new URLSearchParams({ org: orgUuid })
  return `/pools/${poolUuid}/l2v/trash/bulk_delete/?${params}`
}

/**
 * @returns /pools/${poolUuid}/l2v/assets/bulk_update/?org=${orgUuid}
 */
export const getBulkUpdateAssetUrl = ({
  orgUuid,
  poolUuid,
  assetCollectionUuid
}: Required<PoolParams> & { assetCollectionUuid?: string }) => {
  const params = new URLSearchParams({ org: orgUuid })
  if (assetCollectionUuid) params.set("collection_uuid", assetCollectionUuid)
  return `/pools/${poolUuid}/l2v/assets/bulk_update/?${params}`
}

/**
 * @returns /pools/${poolUuid}/l2v/assets/bulk_delete/?org=${orgUuid}
 */
export const getBulkDeleteAssetUrl = ({
  orgUuid,
  poolUuid,
  assetCollectionUuid
}: Required<PoolParams> & { assetCollectionUuid?: string }) => {
  const params = new URLSearchParams({ org: orgUuid })
  if (assetCollectionUuid) params.set("collection_uuid", assetCollectionUuid)
  return `/pools/${poolUuid}/l2v/assets/bulk_delete/?${params}`
}

/**
 * @returns /pools/${poolUuid}/l2v/asset_urls/?org=${orgUuid}
 */
export const getAssetUrlListUrl = ({
  orgUuid,
  poolUuid,
  assetUuid,
  collectionUuid,
  packagingUuid
}: Required<PoolParams> & {
  assetUuid?: string
  collectionUuid?: string
  packagingUuid?: string
}) => {
  const params = new URLSearchParams({ org: orgUuid })
  if (assetUuid) params.set("asset_uuid", assetUuid)
  if (collectionUuid) params.set("collection_uuid", collectionUuid)
  if (packagingUuid) params.set("packaging_uuid", packagingUuid)
  return `/pools/${poolUuid}/l2v/asset_urls/?${params}`
}

/**
 * @returns /pools/${poolUuid}/l2v/asset_urls/export?org=${orgUuid}
 */
export const getAssetUrlListExportUrl = ({
  orgUuid,
  poolUuid,
  assetUuid,
  cdnUuid,
  collectionUuid,
  packagingUuid
}: AssetUrlListExportParams) => {
  const params = new URLSearchParams({ org: orgUuid })
  if (assetUuid) params.set("asset_uuid", assetUuid)
  if (cdnUuid) params.set("cdn_uuid", cdnUuid)
  if (collectionUuid) params.set("collection_uuid", collectionUuid)
  if (packagingUuid) params.set("packaging_uuid", packagingUuid)
  return `/pools/${poolUuid}/l2v/asset_urls/export?${params}`
}

//--------------------------------------------------------------------------------------------------
// Queries & mutations
//--------------------------------------------------------------------------------------------------

export const useAsset = (params?: AssetParams) => {
  const { orgUuid, poolUuid, assetUuid } = useRouteParams(params)
  const url = getAssetUrl({ assetUuid, orgUuid, poolUuid })

  return useGetQuery<api.ott.L2VVODAssetGet>("ott", url)
}

export const useAssetList = (
  params?: PoolParams & PaginationParams & { assetCollectionUuid?: string }
) => {
  const { orgUuid, poolUuid, assetCollectionUuid } = useRouteParams(params)
  const url = getAssetListUrl({ assetCollectionUuid, orgUuid, poolUuid })

  return useGetQuery<api.ott.PaginatedL2VVODAssetGetList>("ott", url)
}

export const useAddAsset = (params?: RecorderParams) => {
  const { orgUuid, poolUuid, recorderUuid } = useRouteParams(params)
  const poolUrl = getPoolUrl({ orgUuid, poolUuid })
  const assetListUrl = getAssetListUrl({ orgUuid, poolUuid })
  const recorderUrl = getRecorderUrl({ orgUuid, poolUuid, recorderUuid })

  type TRequest = DeepPartial<api.ott.L2VVODAssetRequest>
  type TResponse = api.ott.L2VVODAssetGet

  return usePostQuery<TRequest, TResponse>("ott", assetListUrl, [poolUrl, recorderUrl])
}

export const useUpdateAsset = (params?: L2VodParams) => {
  const { poolUuid, orgUuid, recorderUuid, assetCollectionUuid } = useRouteParams(params)
  const assetUrl = getAssetUrl({ assetUuid: params?.assetUuid || "", orgUuid, poolUuid })
  const collectionUrl = getAssetCollectionUrl({
    assetCollectionUuid: assetCollectionUuid || "",
    orgUuid,
    poolUuid
  })
  const assetListUrl = getAssetListUrl({ assetCollectionUuid, orgUuid, poolUuid })
  const recorderUrl = getRecorderUrl({ orgUuid, poolUuid, recorderUuid })
  const listUrl = [
    assetUrl,

    ...(assetCollectionUuid ? [collectionUrl, assetListUrl] : []),
    ...(recorderUuid ? [recorderUrl] : [])
  ]

  type TRequest = DeepPartial<api.ott.L2VVODAssetRequest>
  type TResponse = api.ott.L2VVODAssetGet

  return usePatchQuery<TRequest, TResponse>("ott", assetUrl, listUrl)
}

export const useMoveAssetToTrash = (params?: L2VodParams) => {
  const { poolUuid, orgUuid, recorderUuid, assetCollectionUuid } = useRouteParams(params)
  const { data: pool } = useSuspenseQuery(usePool({ orgUuid, poolUuid }))
  const assetUrl = getAssetUrl({ assetUuid: params?.assetUuid || "", orgUuid, poolUuid })
  const assetListUrl = getAssetListUrl({ assetCollectionUuid, orgUuid, poolUuid })
  const collectionUrl = getAssetCollectionUrl({
    assetCollectionUuid: assetCollectionUuid || "",
    orgUuid,
    poolUuid
  })
  const recorderUrl = getRecorderUrl({ orgUuid, poolUuid, recorderUuid })
  const listUrl = [
    assetUrl,
    getTrashedAssetListUrl({ orgUuid, poolUuid }),
    ...(assetCollectionUuid ? [collectionUrl, assetListUrl] : []),
    ...(recorderUuid ? [recorderUrl] : [])
  ]

  type TRequest = DeepPartial<api.ott.L2VVODAssetRequest>
  type TResponse = api.ott.L2VVODAssetGet

  const { mutationFn, onSuccess } = usePatchQuery<TRequest, TResponse>("ott", assetUrl, listUrl, {
    matchAllSearchParams: true
  })

  return {
    mutationFn: () => mutationFn({ collectionUuid: pool.l2vTrashCollectionUuid }),
    onSuccess
  }
}

export const useDeleteAsset = (params?: L2VodParams) => {
  const { poolUuid, orgUuid, recorderUuid, assetCollectionUuid } = useRouteParams(params)
  const assetUrl = getAssetUrl({ assetUuid: params?.assetUuid || "", orgUuid, poolUuid })
  const assetListUrl = getAssetListUrl({ assetCollectionUuid, orgUuid, poolUuid })
  const collectionUrl = getAssetCollectionUrl({
    assetCollectionUuid: assetCollectionUuid || "",
    orgUuid,
    poolUuid
  })
  const recorderUrl = getRecorderUrl({ orgUuid, poolUuid, recorderUuid })
  const listUrl = [
    assetUrl,
    ...(assetCollectionUuid ? [collectionUrl, assetListUrl] : []),
    ...(recorderUuid ? [recorderUrl] : [])
  ]

  return useDeleteQuery("ott", assetUrl, listUrl)
}

export const useTrashedAsset = (params?: AssetParams) => {
  const { orgUuid, poolUuid } = useRouteParams(params)

  const url = getTrashedAssetUrl({
    assetUuid: params?.assetUuid || "",
    orgUuid,
    poolUuid
  })

  return useGetQuery<api.ott.L2VVODAssetGet>("ott", url)
}

export const useTrashedAssetList = (
  params?: PoolParams & PaginationParams & TrashedAssetQueryParams
) => {
  const { orgUuid, poolUuid } = useRouteParams(params)
  const url = getTrashedAssetListUrl({
    ordering: params?.ordering,
    orgUuid,
    page: params?.page,
    pageSize: params?.pageSize,
    poolUuid,
    search: params?.search
  })

  return useGetQuery<api.ott.PaginatedL2VVODAssetGetList>("ott", url)
}

/**
 * Every list and detail view a bulk asset mutation can affect. Built without a collection filter on
 * purpose: paired with `matchAllSearchParams`, the asset list key then matches every
 * `collection_uuid` variant, so both the source and the target collection refresh.
 */
const useBulkInvalidateUrlList = (params?: L2VodParams) => {
  const { orgUuid, poolUuid, recorderUuid, assetCollectionUuid } = useRouteParams(params)

  return [
    getAssetListUrl({ orgUuid, poolUuid }),
    getAssetCollectionListUrl({ orgUuid, poolUuid }),
    getTrashedAssetListUrl({ orgUuid, poolUuid }),
    ...(assetCollectionUuid
      ? [getAssetCollectionUrl({ assetCollectionUuid, orgUuid, poolUuid })]
      : []),
    ...(recorderUuid ? [getRecorderUrl({ orgUuid, poolUuid, recorderUuid })] : [])
  ]
}

export const useBulkDeleteTrashedAsset = (params?: PoolParams) => {
  const { poolUuid, orgUuid } = useRouteParams(params)
  const bulkAssetUrl = getBulkDeleteTrashedAssetUrl({ orgUuid, poolUuid })
  const trashedAssetListUrl = getTrashedAssetListUrl({ orgUuid, poolUuid })
  const { jsonPost } = useApi("ott")

  type TRequest = DeepPartial<api.ott.L2VTrashBulkDeleteRequest>
  type TResponse = api.ott.L2VTrashBulkDeleteResponse

  return {
    mutationFn: (data?: TRequest) => jsonPost<TResponse>(bulkAssetUrl, data ?? {}),
    onSuccess: async () => {
      await invalidateQueries("ott", [trashedAssetListUrl], { matchAllSearchParams: true })
    }
  }
}

export const useDeleteTrashedAsset = (params?: AssetParams) => {
  const { poolUuid, orgUuid, assetUuid } = useRouteParams(params)
  const assetUrl = getDeleteTrashedAssetUrl({ assetUuid, orgUuid, poolUuid })
  const trashedAssetListUrl = getTrashedAssetListUrl({ orgUuid, poolUuid })

  return useDeleteQuery("ott", assetUrl, [trashedAssetListUrl], { matchAllSearchParams: true })
}

export const useRestoreTrashedAsset = (params?: AssetParams) => {
  const { poolUuid, orgUuid } = useRouteParams(params)
  const trashedAssetUrl = getTrashedAssetUrl({
    assetUuid: params?.assetUuid || "",
    orgUuid,
    poolUuid
  })
  const trashedAssetListUrl = getTrashedAssetListUrl({ orgUuid, poolUuid })

  type TRequest = DeepPartial<api.ott.L2VVODAssetRequest>
  type TResponse = api.ott.L2VVODAssetGet

  return usePatchQuery<TRequest, TResponse>("ott", trashedAssetUrl, [trashedAssetListUrl], {
    matchAllSearchParams: true
  })
}

export const useAssetUrlList = (
  params?: PoolParams & {
    assetUuid?: string
    collectionUuid?: string
    packagingUuid?: string
  }
) => {
  const { orgUuid, poolUuid } = useRouteParams(params)
  const url = getAssetUrlListUrl({
    assetUuid: params?.assetUuid,
    collectionUuid: params?.collectionUuid,
    orgUuid,
    packagingUuid: params?.packagingUuid,
    poolUuid
  })

  return useGetQuery<api.ott.L2VVODAssetURL[]>("ott", url)
}

export const useBulkUpdateAsset = (params?: L2VodParams) => {
  const { orgUuid, poolUuid, assetCollectionUuid } = useRouteParams(params)
  const url = getBulkUpdateAssetUrl({ assetCollectionUuid, orgUuid, poolUuid })
  const toInvalidateUrlList = useBulkInvalidateUrlList(params)
  const { jsonPost } = useApi("ott")

  type TRequest = api.ott.L2VVODAssetBulkUpdateRequest
  type TResponse = api.ott.L2VVODAssetBulkUpdateResponse

  return {
    mutationFn: (data: TRequest) => jsonPost<TResponse>(url, data),
    onSuccess: async () => {
      await invalidateQueries("ott", toInvalidateUrlList, {
        matchAllSearchParams: true
      })
    }
  }
}

export const useBulkDeleteAsset = (params?: L2VodParams) => {
  const { orgUuid, poolUuid, assetCollectionUuid } = useRouteParams(params)
  const url = getBulkDeleteAssetUrl({ assetCollectionUuid, orgUuid, poolUuid })
  const toInvalidateUrlList = useBulkInvalidateUrlList(params)
  const { jsonPost } = useApi("ott")

  type TRequest = api.ott.L2VVODAssetBulkDeleteRequest
  type TResponse = api.ott.L2VVODAssetBulkDeleteResponse

  return {
    mutationFn: (data: TRequest) => jsonPost<TResponse>(url, data),
    onSuccess: async () => {
      await invalidateQueries("ott", toInvalidateUrlList, {
        matchAllSearchParams: true
      })
    }
  }
}
